// Tenant-from-hostname: the pure rule. No imports, no import.meta, so
// it runs under plain tsx (`cd apps/web && npx tsx src/lib/tenantHostRules.test.ts`).
// tenantHost.ts gathers the environment and calls this.
//
// Why the appHost guard exists. On 2026-09-20 the GitHub `dev` variable
// APP_HOST_SUFFIX was `ferrytic.com` while PUBLIC_APP_URL was
// `https://app.ferrytic.com`. The sign-in page at app.ferrytic.com then
// read its OWN hostname as `<app>.<ferrytic.com>`, locked the
// organisation field to "app", and every tester was refused with
// `No organization with slug "app"`. The app's own host is never a
// tenant, whatever the suffix says.

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

export interface TenantHostHint {
  /** Org slug if we could derive one from the hostname. */
  slug: string | null;
  /** Where the slug came from — useful for telling users in the UI why we
   *  pre-selected an org and giving them an out (e.g. "Not your org? Sign
   *  out and visit your org's URL"). */
  source: 'subdomain' | 'env-override' | 'none';
  /** Set when the configuration is self-contradictory (the suffix is a
   *  parent of the app host, so the app host itself would parse as a
   *  tenant). The hint is still `none`; this is for a console warning. */
  misconfiguration?: string;
}

const NONE: TenantHostHint = { slug: null, source: 'none' };

/**
 * @param hostname   window.location.hostname (or a test value)
 * @param suffix     the parent domain tenant subdomains hang off
 *                   (VITE_APP_HOST_SUFFIX, else the app host)
 * @param appHost    hostname of PUBLIC_APP_URL — the product's own host
 * @param envOverride VITE_DEV_TENANT_SLUG, honoured before anything else
 */
export function deriveTenantHint(
  hostname: string,
  suffix: string,
  appHost: string,
  envOverride?: string,
): TenantHostHint {
  const override = envOverride?.trim();
  if (override && SLUG_RE.test(override)) {
    return { slug: override, source: 'env-override' };
  }

  const host = (hostname ?? '').trim().toLowerCase();
  if (!host) return NONE;

  // localhost / *.local / IP addresses — no subdomain meaning.
  if (host === 'localhost' || host.endsWith('.local') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return NONE;
  }

  const sfx = (suffix ?? '').trim().toLowerCase();
  const app = (appHost ?? '').trim().toLowerCase();

  // The product's own host is never a tenant. This holds regardless of
  // what the suffix is set to — see the header comment.
  if (app && host === app) {
    if (sfx && sfx !== app && app.endsWith(`.${sfx}`)) {
      return {
        ...NONE,
        misconfiguration: `APP_HOST_SUFFIX is "${sfx}" but the app is served from "${app}"; tenant subdomains hang off the app host, so the suffix should equal it (or be unset).`,
      };
    }
    return NONE;
  }

  // Match `<something>.<suffix>` only — bare suffix means no tenant.
  if (!sfx || host === sfx || !host.endsWith(`.${sfx}`)) return NONE;

  const candidate = host.slice(0, host.length - sfx.length - 1);
  // Multi-level subdomains (e.g. `foo.bar.<app host>`) aren't a thing —
  // bail rather than guessing which segment is the slug.
  if (candidate.includes('.')) return NONE;
  if (!SLUG_RE.test(candidate)) return NONE;

  return { slug: candidate, source: 'subdomain' };
}
